
import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SchoolsService, formatSchoolAccountLabel } from '@k12/app-services';
import { IUserRecordEditService } from '@k12/admin-services';
import { AppInsightsService } from '@k12/error_handler';
import { HouseholdService } from '@k12/households';
import {
  Address,
  AdminUserType,
  HouseholdRecordVm,
  HouseholdUpdatePayload,
  ProviderData,
  SeverityLevel,
  SchoolRecordVm,
  SubService,
  ToastMessageType,
  toProviderTypeId,
  ProviderTypeId
} from '@k12/models';
import { BreadcrumbService } from '@k12/navigation';
import { ToastMessageService } from '@k12/notifications';
import { toAidProgramTypeIds, UserInfoPanelComponent } from '@k12/user-info';

type UserRecordData = HouseholdRecordVm | ProviderData | SchoolRecordVm;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-user-record-edit',
  imports: [MatButtonModule, MatIconModule, RouterModule, UserInfoPanelComponent],
  styleUrl: './user-record-edit.component.scss',
  templateUrl: './user-record-edit.component.html'
})
export class UserRecordEditComponent implements OnInit, OnDestroy {
  @Input() parentUrl: string = '';
  // Input for services that need to be injected from the consuming application
  @Input() userRecordService: IUserRecordEditService | null = null;
  @Input() userType: AdminUserType = AdminUserType.Provider;

  @Output() cancelled = new EventEmitter<void>();
  @Output() saved = new EventEmitter<ProviderData | HouseholdUpdatePayload | SchoolRecordVm>();

  accountName: string = '';
  loading: boolean = true;
  subServices: SubService[] = [];
  userData: UserRecordData | null = null;
  userId: string;

  readonly adminUserTypes = AdminUserType;
  private readonly appInsightsService = inject(AppInsightsService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly householdRecordService = inject(HouseholdService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly schoolRecordService = inject(SchoolsService);
  private readonly toastMessageService = inject(ToastMessageService);

  ngOnInit(): void {
    this.loading = true;
    if (history.state?.providerData) {
      this.userData = history.state.providerData;
      this.userId = (this.userData as ProviderData)?.id ?? '';
      this.accountName = `${(this.userData as ProviderData).businessName}`;
    } else if (history.state?.schoolData) {
      this.userData = history.state.schoolData;
      this.userId = this.route.snapshot.queryParams['userId'] || '';
      this.accountName = formatSchoolAccountLabel(
        (this.userData as SchoolRecordVm).schoolName,
        (this.userData as SchoolRecordVm).institutionName || (this.userData as SchoolRecordVm).schoolName
      );
    } else if (history.state?.householdData) {
      this.userData = history.state.householdData;
      this.userId = this.route.snapshot.queryParams['userId'] || '';
      this.accountName = `${(this.userData as HouseholdRecordVm).firstName} ${(this.userData as HouseholdRecordVm).lastName}`;
    }
    this.getEmployeeSubServices();
  }

  ngOnDestroy(): void {
    this.breadcrumbService.clearDynamicBreadcrumbs();
  }

  getEmployeeSubServices(): void {
    if (this.userType !== AdminUserType.Provider) return;
    if (!this.userRecordService) return;

    this.userRecordService.getSubServices().subscribe((res: SubService[]) => {
      this.subServices = res;
    });
  }

  onSave(userData: UserRecordData): void {
    this.updateUserDetails(userData);
  }

  onCancel(): void {
    this.returnToUserRecord();
    this.cancelled.emit();
  }

  updateUserDetails(userData: UserRecordData): void {
    switch (this.userType) {
      case AdminUserType.Households:
        this.updateHouseholdDetails(userData as HouseholdRecordVm);
        break;
      case AdminUserType.Schools:
        this.updateSchoolDetails(userData as SchoolRecordVm);
        break;
      case AdminUserType.Provider:
      default:
        this.updateProviderDetails(userData as ProviderData);
        break;
    }
  }

  private updateHouseholdDetails(userData: HouseholdRecordVm): void {
    const householdData = this.userData as HouseholdRecordVm;
    const payload = {
      address1: userData.address1,
      address2: userData.address2,
      city: userData.city,
      countryCode: 1,
      county: userData.county,
      dob: userData.dob ? new Date(userData.dob).toISOString() : undefined,
      firstName: userData.firstName,
      householdAccountId: householdData.householdAccountId,
      householdIncome: userData.householdIncome,
      householdMemberCount: userData.householdMemberCount,
      id: householdData?.id ?? '',
      incomeVerificationRequired: userData.incomeVerificationRequired,
      ncResidency: userData.ncResidency,
      lastName: userData.lastName,
      middleName: userData.middleName,
      personId: householdData?.personId,
      phoneNumber: userData.phoneNumber?.replaceAll('-', '') || '',
      phoneTypeId: 2,
      programPartnerId: this.userId,
      prefix: householdData?.prefix,
      state: userData.state,
      ssn: userData.ssn ? userData.ssn.replaceAll('-', '') : '',
      suffix: householdData?.suffix,
      zip: userData.zip
    };

    this.householdRecordService.updateHousehold(payload).subscribe({
      next: () => {
        this.toastMessageService.showToast(ToastMessageType.Success, 'Household information saved successfully');
        this.returnToUserRecord(userData);
        this.saved.emit(userData);
      },
      error: (error: unknown) => {
        console.error('Error updating household details:', error);
        this.handleError(String(error), 'Failed to save household user information');
      }
    });
  }

  private async updateProviderDetails(userData: ProviderData): Promise<void> {
    const payload = {
      addresses:
        userData.addresses?.map((address: Address) => ({
          addressType: 1,
          address1: address.address1,
          address2: address.address2,
          city: address.city,
          id: address.id,
          state: address.state,
          zip: address.zip,
          county: address.county
        })) || [],
      businessName: userData.businessName,
      email: userData.email,
      firstName: userData.firstName,
      id: userData.id,
      lastName: userData.lastName,
      ownerAccountId: userData.ownerAccountId,
      phone: userData.phone,
      secondaryPhone: userData.secondaryPhone,
      providerTypeId: toProviderTypeId(userData.type ?? userData.providerType) ?? ProviderTypeId.Therapy,
      providerSizeId: userData.providerSizeId ?? 1,
      npiNumber: userData.npiNumber,
      npiNumber2: userData.npiNumber2,
      licenseNumber: userData.licenseNumber,
      issuingState: userData.issuingState
    };

    const observable = await this.userRecordService?.editProviderDetails(payload);
    observable?.subscribe({
      next: () => {
        this.toastMessageService.showToast(ToastMessageType.Success, `Data has been saved for this user`);
        this.returnToUserRecord(userData);
        this.saved.emit(userData);
      },
      error: (error: unknown) => {
        console.error('Error updating user details:', error);
        this.handleError(String(error), 'Failed to save provider user information');
      }
    });
  }

  private updateSchoolDetails(userData: SchoolRecordVm): void {
    const address = userData.addresses?.[0];
    const schoolData = this.userData as SchoolRecordVm;
    const programTypeIds = toAidProgramTypeIds(userData.aidProgramEligibility);
    const payload = {
      institutionId: schoolData.institutionId,
      // original values from GET
      schoolTypeId: schoolData.schoolTypeId,
      operationalScheduleTypeId: schoolData.operationalScheduleTypeId ?? 1,
      backgroundCheckNameOnFile: schoolData.backgroundCheckNameOnFile ?? '',
      tuitionFeesUploadedDate: schoolData.tuitionFeesUploadedDate,
      areTuitionFeesApproved: schoolData.areTuitionFeesApproved,
      verbalCommunication: schoolData.verbalCommunication ?? false,
      // edited values
      hdmaContact: userData.hdmaContact,
      hdmaFirstName: userData.hdmaFirstName,
      hdmaMiddleName: userData.hdmaMiddleName,
      hdmaLastName: userData.hdmaLastName,
      phone: userData.phone,
      federalEmployerID: userData.federalEmployerID ?? schoolData.federalEmployerID,
      feinNumber: userData.feinNumber,
      dnpeNumber: userData.dnpeNumber,
      schoolStatusId: userData.schoolStatusId ?? schoolData.schoolStatusId,
      backgroundCheckValidatedDate: schoolData.backgroundCheckValidationDate,
      programTypeIds,
      programTypeId: programTypeIds[0],
      instructionTypeIds: Array.isArray(userData.educationDeliveryMethod) ? userData.educationDeliveryMethod : [],
      fiscalYearEndDate: userData.fiscalYearEndDate ? new Date(userData.fiscalYearEndDate).toISOString() : null,
      businessAddressId: address?.id || null,
      address1: address.address1,
      address2: address.address2,
      city: address.city,
      county: address.county,
      state: address.state,
      zip: address.zip
    };

    this.schoolRecordService.updateSchool(this.userId, payload).subscribe({
      next: () => {
        this.toastMessageService.showToast(ToastMessageType.Success, 'School information saved successfully');
        this.returnToUserRecord(userData);
        this.saved.emit(userData);
      },
      error: (error: unknown) => {
        this.handleError(String(error), 'Failed to save school user information');
      }
    });
  }

  private returnToUserRecord(userData?: UserRecordData): void {
    let partnerTypeId = 1;
    if (this.userType === AdminUserType.Provider) {
      partnerTypeId = 2;
    } else if (this.userType === AdminUserType.Households) {
      partnerTypeId = 3;
    }
    this.router.navigate([this.parentUrl, this.userId], {
      relativeTo: this.route,
      queryParams: {
        partnerTypeId,
        accountName: this.accountName,
        ...(userData && { refresh: Date.now() })
      },
      replaceUrl: true
    });
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  private handleError(logMessage: string, toastMessage: string): void {
    this.appInsightsService.log(
      new Error(`User Record Edit household details client error: ${logMessage}`),
      SeverityLevel.error
    );
    this.toastMessageService.showToast(ToastMessageType.Error, toastMessage);
  }
}
